import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import {
  mdiAlertCircle,
  mdiCancel,
  mdiCheck,
  mdiCheckCircle,
  mdiPause,
  mdiPlay,
  mdiRefresh,
  mdiSkipNext,
} from '@mdi/js';
import * as React from 'react';
import type { ImportJobProgress } from '../hooks/useImportJobs';
import { JobItemsDetail } from './JobItemsDetail';

interface JobProgressProps {
  job: ImportJobProgress;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onNewImport: () => void;
  isPausing: boolean;
  isResuming: boolean;
  isCancelling: boolean;
  isRetrying: boolean;
}

export const JobProgress: React.FC<JobProgressProps> = ({
  job,
  onPause,
  onResume,
  onCancel,
  onRetry,
  onNewImport,
  isPausing,
  isResuming,
  isCancelling,
  isRetrying,
}) => {
  const isProcessing = job.status === 'processing' || job.status === 'pending';
  const isPaused = job.status === 'paused';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isCancelled = job.status === 'cancelled';

  const getStatusColor = () => {
    switch (job.status) {
      case 'processing':
      case 'pending':
        return 'bg-blue-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-grey-500';
      default:
        return 'bg-grey-500';
    }
  };

  const getStatusText = () => {
    switch (job.status) {
      case 'processing':
        return 'Processing...';
      case 'pending':
        return 'Starting...';
      case 'paused':
        return 'Paused';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return job.status;
    }
  };

  return (
    <div className='space-y-6'>
      <div className='text-center'>
        <div className='flex items-center justify-center space-x-2 mb-2'>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${getStatusColor()}`}
          >
            {isProcessing && (
              <span className='animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
            )}
            {getStatusText()}
          </span>
        </div>
        <p className='text-sm text-grey-500'>Job #{job.id}</p>
      </div>

      <div className='relative pt-1'>
        <div className='flex mb-2 items-center justify-between'>
          <div>
            <span className='text-xs font-semibold inline-block text-blue-600'>
              {job.percentComplete}%
            </span>
          </div>
          <div className='text-right'>
            <span className='text-xs font-semibold inline-block text-blue-600'>
              {job.processedItems.toLocaleString()} /{' '}
              {job.totalItems.toLocaleString()}
            </span>
          </div>
        </div>
        <div className='overflow-hidden h-3 text-xs flex rounded-full bg-grey-200'>
          <div
            style={{ width: `${job.percentComplete}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${getStatusColor()}`}
          />
        </div>
      </div>

      <div className='grid grid-cols-3 gap-4'>
        <div className='bg-green-50 rounded-lg p-4 text-center'>
          <div className='flex items-center justify-center mb-1'>
            <Icon path={mdiCheckCircle} size={20} className='text-green-600' />
          </div>
          <p className='text-2xl font-bold text-green-700'>
            {job.successfulItems.toLocaleString()}
          </p>
          <p className='text-xs text-green-600'>Successful</p>
        </div>
        <div className='bg-red-50 rounded-lg p-4 text-center'>
          <div className='flex items-center justify-center mb-1'>
            <Icon path={mdiAlertCircle} size={20} className='text-red-600' />
          </div>
          <p className='text-2xl font-bold text-red-700'>
            {job.failedItems.toLocaleString()}
          </p>
          <p className='text-xs text-red-600'>Failed</p>
        </div>
        <div className='bg-grey-50 rounded-lg p-4 text-center'>
          <div className='flex items-center justify-center mb-1'>
            <Icon path={mdiSkipNext} size={20} className='text-grey-600' />
          </div>
          <p className='text-2xl font-bold text-grey-700'>
            {job.skippedItems.toLocaleString()}
          </p>
          <p className='text-xs text-grey-600'>Skipped</p>
        </div>
      </div>

      <div className='flex justify-center space-x-3 pt-4 border-t'>
        {isProcessing && (
          <Button
            label='Pause'
            icon={mdiPause}
            color='secondary'
            outline
            onClick={onPause}
            disabled={isPausing}
          />
        )}
        {isPaused && (
          <>
            <Button
              label='Resume'
              icon={mdiPlay}
              color='success'
              onClick={onResume}
              disabled={isResuming}
            />
            <Button
              label='Cancel'
              icon={mdiCancel}
              color='danger'
              outline
              onClick={onCancel}
              disabled={isCancelling}
            />
          </>
        )}
        {(isCompleted || isFailed || isCancelled) && (
          <>
            {job.failedItems > 0 && (
              <Button
                label='Retry Failed'
                icon={mdiRefresh}
                color='secondary'
                onClick={onRetry}
                disabled={isRetrying}
              />
            )}
            <Button
              label='New Import'
              icon={mdiCheck}
              color='primary'
              onClick={onNewImport}
            />
          </>
        )}
      </div>

      <JobItemsDetail
        jobId={job.id}
        failedCount={job.failedItems}
        skippedCount={job.skippedItems}
      />
    </div>
  );
};
